<template>
    <div class="heatmap-container">
        <div ref="heatmapChart" class="heatmap-chart"></div>
        <div class="heatmap-description">
            <i class="fas fa-info-circle"></i>&nbsp;
            <span>
                각 셀의 값은 평균 사용량을 나타냅니다.
                {{
                    selectedPeriod === "yearly"
                        ? "연간 에너지 사용 패턴은 여름철 부하 시간대를 기준으로 분석됩니다."
                        : ""
                }}
            </span>
        </div>
    </div>
</template>

<script>
import * as echarts from "echarts";

export default {
    name: "BillLoadSectionHeatmap",
    props: {
        data: {
            type: Object,
        },
        selectedPeriod: {
            type: String,
            default: "monthly",
        },
        activeTab: {
            type: Number,
            default: 0,
        },
    },
    computed: {
        heatmapData() {
            return this.data?.loadSectionCostRatio?.heatmap;
        },
        getPeakType() {
            if (!this.data?.loadSectionCostRatio?.timeSlotInfo) return [];
            const timeSlotInfo = this.data?.loadSectionCostRatio?.timeSlotInfo;
            let formattedTimeSlotInfo = [];
            formattedTimeSlotInfo = timeSlotInfo.map((item) => {
                return {
                    ...item,
                    hour: this.formatHourWithKorean(item.hour),
                };
            });
            return formattedTimeSlotInfo;
        },
        loadTypeColors() {
            return {
                lowPeak: "#22c55e", // 초록색 - 경부하
                midPeak: "#f59e0b", // 주황색 - 중부하
                highPeak: "#dc2626", // 빨간색 - 최대부하
                // 대문자 버전 호환성 유지
                LowPeak: "#22c55e",
                MidPeak: "#f59e0b",
                HighPeak: "#dc2626",
            };
        },
        chartOption() {
            if (!this.heatmapData) return null;

            const { value, xSeries, ySeries } = this.heatmapData;

            // value 배열을 ECharts 히트맵 형식으로 변환
            const heatmapData = value.map((item) => [item[0], item[1], item[2]]);

            // 최대값과 최소값 계산
            const values = heatmapData.map((item) => item[2]);
            const maxValue = Math.max(...values);
            const minValue = Math.min(...values);

            return {
                tooltip: {
                    position: "top",
                    formatter: function (params) {
                        const x = xSeries[params.value[0]];
                        const y = ySeries[params.value[1]];

                        return ` ${params.marker} ${x} ${y} 사용량 &nbsp;&nbsp;&nbsp;<b>${params.value[2]} kWh</b>`;
                    },
                },
                grid: {
                    height: "87%",
                    bottom: "3%",
                    left: "5%",
                    right: "2%",
                },
                xAxis: {
                    type: "category",
                    data: xSeries,
                    position: "top",
                    axisLine: {
                        show: false,
                    },
                    axisTick: {
                        show: false,
                    },
                    splitLine: {
                        show: false,
                    },
                    axisLabel: {
                        fontSize: 10,
                        interval: 0,
                        formatter: (value, index) => {
                            return `{label${index}|${value}}`;
                        },
                        rich: this.createRichStyles(xSeries),
                    },
                },
                yAxis: {
                    type: "category",
                    data: ySeries,
                    axisLine: {
                        show: false,
                    },
                    axisTick: {
                        show: false,
                    },
                    splitLine: {
                        show: false,
                    },
                    axisLabel: {
                        fontSize: 11,
                    },
                },
                visualMap: {
                    show: false,
                    min: minValue,
                    max: maxValue,
                    calculable: true,
                    orient: "horizontal",
                    left: "center",
                    bottom: "5%",
                    inRange: {
                        color: ["#e3f2fd", "#1976d2", "#0d47a1"],
                    },
                    text: ["높음", "낮음"],
                    textStyle: {
                        fontSize: 10,
                    },
                },
                series: [
                    {
                        name: "사용량",
                        type: "heatmap",
                        data: heatmapData,
                        itemStyle: {
                            borderWidth: 4,
                            borderColor: "#fff",
                            borderRadius: 10,
                        },
                        label: {
                            show: true,
                            fontSize: 9,
                            color: "white",
                            formatter: (params) => {
                                const value = params.data[2];
                                if (value == null || Number.isNaN(value)) return "";
                                // 소량 사이트에서는 작은 값도 라벨을 숨기지 않음
                                if (maxValue >= 1 && value < maxValue * 0.1) return "";
                                if (value === 0) return "0";
                                if (maxValue < 0.1) return value.toFixed(3);
                                if (maxValue < 1) return value.toFixed(2);
                                return value % 1 === 0 ? value.toString() : value.toFixed(1);
                            },
                        },
                        emphasis: {
                            itemStyle: {
                                shadowBlur: 10,
                                shadowColor: "rgba(0, 0, 0, 0.5)",
                            },
                        },
                    },
                ],
            };
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.initChart();
        });
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.dispose();
        }
    },
    watch: {
        activeTab: {
            handler() {
                this.initChart();
            },
            deep: true,
        },
        chartOption: {
            handler() {
                this.updateChart();
            },
            deep: true,
        },
    },
    methods: {
        formatHourWithKorean(hourArray) {
            return `${parseInt(hourArray)}시`;
        },
        getLoadTypeForHour(hour) {
            const peakType = this.getPeakType.find((item) => item.hour === hour);
            return peakType ? peakType.loadType : "lowPeak";
        },
        getColorForHour(hour) {
            const loadType = this.getLoadTypeForHour(hour);
            return this.loadTypeColors[loadType] || this.loadTypeColors.LowPeak;
        },
        createRichStyles(xSeries) {
            const richStyles = {};
            xSeries.forEach((hour, index) => {
                const color = this.getColorForHour(hour);
                richStyles[`label${index}`] = {
                    backgroundColor: color,
                    color: "#fff",
                    padding: [6, 6, 6, 6],
                    borderRadius: 4,
                    fontWeight: "bold",
                    fontSize: 10,
                };
            });
            return richStyles;
        },
        initChart() {
            if (this.$refs.heatmapChart && this.chartOption) {
                this.chart = echarts.init(this.$refs.heatmapChart);
                this.chart.setOption(this.chartOption);

                // 창 크기 변경 시 차트 리사이즈
                window.addEventListener("resize", this.handleResize);
            }
        },
        updateChart() {
            if (this.chart && this.chartOption) {
                this.chart.setOption(this.chartOption, true);
            }
        },
        handleResize() {
            if (this.chart) {
                this.chart.resize();
            }
        },
    },
};
</script>

<style scoped>
.heatmap-description {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    font-size: 12px;
    color: #bdbdbd;
}
.heatmap-container {
    width: 100%;
    height: 95%;
    min-height: 250px;
    padding: 0;
    background: transparent;
    border-radius: 0;
    border: none;
}

.heatmap-chart {
    width: 100%;
    height: 100%;
    min-height: 250px;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
